import config from '../../config/config';
import { sessionAwareFetch } from './sessionAwareFetch';

export const servicesService = {

  // get services with optional params (serviceType, subServiceType, priceType, and with or without biz info)
  async getServices(service, subservice, priceType, includeBusinesses, excludeCustom) {
    const params = new URLSearchParams();

    if (service) {
      params.set('ServiceType', encodeURIComponent(service));
    }
    if (subservice) {
      params.set('SubServiceType', encodeURIComponent(subservice));
    }
    if (priceType) {
      params.set('PriceType', encodeURIComponent(priceType));
    }
    if (includeBusinesses) {
      params.set('includeBusinesses', encodeURIComponent(includeBusinesses));
    }
    if (excludeCustom) {
      params.set('excludeCustom', 'true');
    }

    const response = await sessionAwareFetch(
      `${config.apimUrl}/service-api/?${params.toString()}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Ocp-Apim-Subscription-Key': config.apimKeyServicesService,
        },
      },
    );

    if (!response.ok) {
      throw new Error('Failed to fetch services');
    }

    return await response.json();
  },

  async createService(payload) {
    const response = await sessionAwareFetch(`${config.apimUrl}/service-api/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Ocp-Apim-Subscription-Key': config.apimKeyServicesService,
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(
        errBody.error || `Failed to create service (HTTP ${response.status})`,
      );
    }

    return await response.json();
  },

  /**
   * PATCH /service-api/{ServiceId}?BusinessId=...
   * Partial update. Send EITHER service-level fields (Quoting / IsSchedulable)
   * OR a single SubService object — not both in the same call (per UI convention).
   *
   * For sub-services, the SubService object FULLY REPLACES any existing sub-service
   * with the same SubServiceType. Always send every field you want preserved.
   *
   * @param {string} businessId
   * @param {string} serviceId
   * @param {object} body - UpdateServiceRequest
   *   { Quoting?, IsSchedulable?, SubService?: { SubServiceType, Tagline?, Pricing } }
   * @returns {Promise<object>} Full updated ServiceResponse
   */
  async patchService(businessId, serviceId, body) {
    const params = new URLSearchParams();
    params.set('BusinessId', businessId);

    const response = await sessionAwareFetch(
      `${config.apimUrl}/service-api/${encodeURIComponent(serviceId)}?${params.toString()}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Ocp-Apim-Subscription-Key': config.apimKeyServicesService,
        },
        credentials: 'include',
        body: JSON.stringify(body),
      },
    );

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(
        errBody.error || `Failed to update service (HTTP ${response.status})`,
      );
    }

    return await response.json();
  },

  /**
   * DELETE /service-api/{ServiceId}?BusinessId=...
   * Deletes the entire service (unenrolls the business from the trade and removes all sub-services).
   *
   * @param {string} businessId
   * @param {string} serviceId
   * @returns {Promise<true>}
   */
  async deleteService(businessId, serviceId) {
    const params = new URLSearchParams();
    params.set('BusinessId', businessId);

    const response = await sessionAwareFetch(
      `${config.apimUrl}/service-api/${encodeURIComponent(serviceId)}?${params.toString()}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Ocp-Apim-Subscription-Key': config.apimKeyServicesService,
        },
        credentials: 'include',
      },
    );

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(
        errBody.error || `Failed to delete service (HTTP ${response.status})`,
      );
    }

    return true;
  },

  /**
   * DELETE /service-api/{ServiceId}?BusinessId=...&subServiceType=...
   * Deletes a single sub-service from a service. If this leaves the service with
   * zero sub-services, the API automatically deletes the entire service.
   *
   * @param {string} businessId
   * @param {string} serviceId
   * @param {string} subServiceType
   * @returns {Promise<true>}
   */
  async deleteSubService(businessId, serviceId, subServiceType) {
    const params = new URLSearchParams();
    params.set('BusinessId', businessId);
    params.set('SubServiceType', subServiceType);

    const response = await sessionAwareFetch(
      `${config.apimUrl}/service-api/${encodeURIComponent(serviceId)}?${params.toString()}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Ocp-Apim-Subscription-Key': config.apimKeyServicesService,
        },
        credentials: 'include',
      },
    );

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(
        errBody.error || `Failed to delete sub-service (HTTP ${response.status})`,
      );
    }

    return true;
  },
};
